import React, { useEffect } from 'react';
import CustomModal from '../common-components/CustomModal';
import FrisbiiEmbedded from '../features/payment-features/FrisbiiEmbedded';
import { ReactComponent as BigWarningCross } from '../layout/icons/white-cross-in-red-circle-big.svg';
import { ReactComponent as WarningCross } from '../layout/icons/white-cross-in-red-circle.svg';
import ContentStore from '../stores/ContentStore';
import { BalanceType } from '../swagger/api';

export default function useSettlementFlow(contentStore: ContentStore) {
  const {
    balanceStore,
    modalStore,
    loadingStore,
    userStore,
    manageUserAccessStore,
    transactionsStore,
    paymentStore
  } = contentStore.rootStore;
  const { currentAccount } = userStore;
  const { balance } = balanceStore;

  const refreshData = async () => {
    if (!currentAccount?.id) return;
    loadingStore.enableGlobalSpinner();
    try {
      await balanceStore.getBalance(currentAccount.id);
      balanceStore.clearSettlement();
      if (manageUserAccessStore.isPrivateCustomer()) {
        await transactionsStore.getAccountTransactions();
      } else {
        await Promise.all([paymentStore.getInvoices(), paymentStore.getCardPayments()]);
      }
    } finally {
      loadingStore.disableGlobalSpinner();
    }
  };

  useEffect(
    () => () => {
      balanceStore.stopPolling();
    },
    [balanceStore]
  );

  const openFrisbiiModal = (sessionUrl: string) => {
    if (!currentAccount?.id) return;
    const isNegativeBalance = balance?.balanceType === BalanceType.Negative;
    const isPositiveBalance = balance?.balanceType === BalanceType.Positive;
    const isBusiness = !manageUserAccessStore.isPrivateCustomer();
    const suffix = isBusiness ? 'Business' : '';

    const cmsKey = isPositiveBalance
      ? `SettleBalanceModalCharge${suffix}`
      : `SettleBalanceModal${suffix}`;
    const cmsContent = contentStore.cmsStore.getContent(cmsKey);
    const header = cmsContent?.content;
    const subText = cmsContent?.subText;

    const modalContent = (
      <CustomModal
        header={header}
        contentText={subText}
        closable
        removeHorizontalPaddingForChildElement
        onCloseClick={async () => {
          if (currentAccount?.id) {
            try {
              await balanceStore.cancelBalanceSettlement(currentAccount.id);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Failed to cancel settlement when closing Frisbii modal:', error);
            }
          }
        }}
      >
        <FrisbiiEmbedded
          sessionUrl={sessionUrl}
          balanceStore={balanceStore}
          customerId={currentAccount.id}
          isNegativeBalance={isNegativeBalance}
          modalStore={modalStore}
          loadingStore={loadingStore}
          cmsStore={contentStore.cmsStore}
        />
      </CustomModal>
    );
    modalStore.openModal(modalContent);
  };

  const handleSettlementClick = async () => {
    if (!currentAccount?.id || !balance) return;

    try {
      loadingStore.enableGlobalSpinner();
      const result = await balanceStore.initiateSettlement(currentAccount.id, balance);

      if (result.requiresReload) {
        await refreshData();
        return;
      }

      if (!result.sessionUrl) {
        loadingStore.disableGlobalSpinner();
        const errorHeader = contentStore.cmsStore.getContent(
          'SettlementSessionErrorModal'
        )?.content;
        const errorText = contentStore.cmsStore.getContent('SettlementSessionErrorModal')?.subText;
        modalStore.openModal(
          <CustomModal
            topIconDesktop={<WarningCross />}
            topIconMobile={<BigWarningCross />}
            isHeaderRed
            header={errorHeader}
            contentText={errorText}
            primaryButtonText={contentStore.cmsStore.getContent('Ok')?.content}
            onPrimaryButtonClick={() => modalStore.closeModal()}
            closable
          />
        );
        return;
      }

      loadingStore.disableGlobalSpinner();
      openFrisbiiModal(result.sessionUrl);
    } catch (error) {
      loadingStore.disableGlobalSpinner();
      // eslint-disable-next-line no-console
      console.error('Failed to create settlement:', error);
      const errorHeader = contentStore.cmsStore.getContent('SettlementSessionErrorModal')?.content;
      const errorText = contentStore.cmsStore.getContent('SettlementSessionErrorModal')?.subText;
      modalStore.openModal(
        <CustomModal
          topIconDesktop={<WarningCross />}
          topIconMobile={<BigWarningCross />}
          isHeaderRed
          header={errorHeader}
          contentText={errorText}
          primaryButtonText={contentStore.cmsStore.getContent('Ok')?.content}
          onPrimaryButtonClick={() => modalStore.closeModal()}
          closable
        />
      );
    }
  };

  return { handleSettlementClick };
}
